import { useMemo } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '@/helpers/featureFlags'
import type { ListingNotesByListingId } from '@/queries/listingNotesQuery'
import { useListingNotes } from '@/queries/listingNotesQuery'
import type { SearchResult } from '@/queries/listingSearchQuery'
import type { WatchedListingEntry } from '@/queries/watchlistQuery'

const isThreeMpListing = (listing: SearchResult) =>
  listing.siteAbb.toLowerCase() === 'tmp'

const notesFromWatchlist = (
  watchlistEntries: WatchedListingEntry[] | undefined
): ListingNotesByListingId =>
  Object.fromEntries(
    (watchlistEntries ?? [])
      .filter((e): e is WatchedListingEntry & { note: string } => !!e.note)
      .map((e) => [e.listingId, e.note])
  )

type GridNotesParams = {
  personId: string | undefined
  listings: SearchResult[]
  watchlistEntries: WatchedListingEntry[] | undefined
  isWatchlistHeartEnabled: boolean
}

/**
 * Active note text by listing id for the cards on one grid page. With
 * ENABLE_LISTING_NOTES and NOTES_OUTLIVE_WATCHLIST on, it makes one
 * listing-notes batch read for the page's 3MP listings, so un-watched listings
 * get their notes too; until that read succeeds, and whenever either flag is
 * off, it uses the notes on the buyer's Watchlist entries (no extra request).
 */
export const useGridNotesById = ({
  personId,
  listings,
  watchlistEntries,
  isWatchlistHeartEnabled,
}: GridNotesParams): ListingNotesByListingId => {
  const enableListingNotes = useFlag(FeatureFlags.ENABLE_LISTING_NOTES)
  const notesOutliveWatchlist = useFlag(FeatureFlags.NOTES_OUTLIVE_WATCHLIST)
  const readsNotes =
    isWatchlistHeartEnabled && enableListingNotes && notesOutliveWatchlist

  const listingIds = useMemo(
    () =>
      readsNotes
        ? listings.filter(isThreeMpListing).map((l) => l.listingId)
        : [],
    [readsNotes, listings]
  )
  const { data: listingNotes } = useListingNotes(personId, listingIds)

  const watchlistNotes = useMemo(
    () => notesFromWatchlist(watchlistEntries),
    [watchlistEntries]
  )

  if (readsNotes && listingNotes) return listingNotes
  return watchlistNotes
}
